/**
 * Componente do Dashboard de KPIs do fluxo - Versão Premium
 * Cards com gradiente, shimmer no hover e ícones com fundo arredondado
 * Recebe dados consolidados da RPC get_flow_results_kpi
 * 
 * @release v3.0.0 - 2026-03-08
 */

import { Eye, Users, CheckCircle, Clock, type LucideIcon } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { FlowResultsKpiData } from '@/hooks/useFlowResultsKpi';

/** Variantes visuais dos cards de KPI */
type KpiVariant = 'info' | 'primary' | 'success' | 'purple';

/** Estilos de gradiente e cor do ícone por variante */
const VARIANT_STYLES: Record<KpiVariant, string> = {
  info: 'from-sky-500/10 to-sky-500/5 text-sky-600 dark:text-sky-400',
  primary: 'from-primary/10 to-primary/5 text-primary',
  success: 'from-emerald-500/10 to-emerald-500/5 text-emerald-600 dark:text-emerald-400',
  purple: 'from-violet-500/10 to-violet-500/5 text-violet-600 dark:text-violet-400',
};

/** Props de um card de KPI individual */
interface KpiCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  variant: KpiVariant;
  subValue?: string;
}

/** Card de KPI premium com shimmer e gradiente */
function KpiCard({ title, value, icon: Icon, variant, subValue }: KpiCardProps) {
  return (
    <Card className={cn(
      'relative overflow-hidden border-border/50 bg-gradient-to-br from-background to-muted/10 p-4',
      'group hover:shadow-md transition-all duration-300',
    )}>
      {/* Efeito shimmer no hover */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />

      <div className="relative flex items-center gap-3">
        {/* Ícone com fundo arredondado e gradiente */}
        <div className={cn(
          'p-2.5 rounded-xl bg-gradient-to-br',
          VARIANT_STYLES[variant],
        )}>
          <Icon className="h-5 w-5" />
        </div>

        {/* Valor e label */}
        <div>
          <p className="text-xs text-muted-foreground font-medium">{title}</p>
          <p className="text-2xl font-bold text-foreground">{value}</p>
          {subValue && (
            <p className="text-[10px] text-muted-foreground mt-0.5">{subValue}</p>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Formata duração em minutos/segundos */
export function formatDuration(seconds: number | null): string {
  if (!seconds) return '-';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
}

/** Props do dashboard de KPIs - recebe dados consolidados da RPC */
interface FlowResultsDashboardProps {
  kpi: FlowResultsKpiData | null;
  formatDuration: (seconds: number | null) => string;
}

export function FlowResultsDashboard({ kpi, formatDuration }: FlowResultsDashboardProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Visualizações */}
      <KpiCard
        title="Visualizações"
        value={kpi?.views ?? 0}
        icon={Eye}
        variant="info"
      />

      {/* Sessões Iniciadas (unificado) */}
      <KpiCard
        title="Sessões Iniciadas"
        value={kpi?.total_sessions ?? 0}
        icon={Users}
        variant="primary"
      />

      {/* Taxa de Conclusão */}
      <KpiCard
        title="Taxa de Conclusão"
        value={`${kpi?.completion_rate ?? 0}%`}
        icon={CheckCircle}
        variant="success"
        subValue={`${kpi?.completed_count ?? 0} de ${kpi?.total_sessions ?? 0}`}
      />

      {/* Tempo Médio */}
      <KpiCard
        title="Tempo Médio"
        value={formatDuration(kpi?.avg_duration_seconds ?? null)}
        icon={Clock}
        variant="purple"
        subValue="até conclusão"
      />
    </div>
  );
}
